import { Search } from '@carbon/icons-react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React from 'react';

import { ControlledPagination } from 'DesignSystem/features/pagination/controlled-pagination';
import { IconInput } from 'DesignSystem/ui/icon-input';
import { Skeleton } from 'DesignSystem/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableHeaderRow, TableRow } from 'DesignSystem/ui/table';
import { MetaProps } from 'DesignSystem/ui/table-meta-props';
import { cn } from 'DesignSystem/utils';

import { useEventDetails } from '../event-detail.route';
import { useEventItems } from '../hooks/use-event-items';
import EventItemsApprovedSubstitutes from './event-items-approved-substitutes';
import { getEventItemsColumns } from './event-items-columns';
import EventItemsDohCards from './event-items-doh-cards';
import EventItemsFilters from './event-items-filters';

export default function EventItems() {
  const data = useEventDetails();
  const eventId = data?.event?.event_id;

  if (!eventId) {
    return null;
  }

  return <EventItemsTable eventId={eventId} productsAffected={data.event?.products_affected ?? 0} />;
}

function EventItemsTable({ eventId, productsAffected }: { eventId: string; productsAffected: number }) {
  const {
    items,
    resultSetSize,
    dohRollup,
    isLoading,
    isFetching,
    pageNumber,
    pageSize,
    sort,
    searchInput,
    hasActiveSearch,
    hasItemListFilter,
    setSearchInput,
    setPageNumber,
    setPageSize,
    setSort,
  } = useEventItems(eventId);

  const [expandedIds, setExpandedIds] = React.useState<Record<string, boolean>>({});
  const handleToggleExpanded = React.useCallback((id: string) => {
    setExpandedIds((current) => ({ ...current, [id]: !current[id] }));
  }, []);
  const columns = React.useMemo(
    () => getEventItemsColumns({ sort, onSort: setSort, expandedIds, onToggleExpanded: handleToggleExpanded }),
    [expandedIds, handleToggleExpanded, setSort, sort]
  );
  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getRowId: (row) => row.client_item_master_id,
  });
  const columnCount = table.getAllColumns().length;
  const isEmpty = !isLoading && items.length === 0;
  const emptyMessage =
    hasItemListFilter || hasActiveSearch
      ? 'No items match the selected filters'
      : 'No items have been matched to this event yet';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <IconInput
          icon={<Search />}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          className="mb-0.5 w-72"
          type="search"
          sz="sm"
          placeholder="Filter by SKU IDs & Description"
        />
        <EventItemsFilters />
      </div>
      <EventItemsDohCards dohRollup={dohRollup} />
      {hasItemListFilter ? (
        <p className="type-body-sm text-zinc-500">
          Showing {resultSetSize} of {productsAffected} items
        </p>
      ) : null}
      <Table>
        <TableHeader sticky>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const tableCellProps = (header.column.columnDef.meta as MetaProps | undefined)?.tableCellProps ?? {};
                const { className: cellClassName, ...restTableCellProps } = tableCellProps;
                return (
                  <TableHead
                    key={header.id}
                    className={cn('text-nowrap', tableCellProps.align === 'right' && 'text-right', cellClassName)}
                    {...restTableCellProps}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableHeaderRow>
          ))}
        </TableHeader>
        <TableBody>
          {isFetching ? (
            <TableRow>
              <TableCell className="h-1.5 p-0" colSpan={columnCount}>
                <Skeleton className="h-full w-full rounded-none bg-gradient-to-l from-primary to-primary-purple" />
              </TableCell>
            </TableRow>
          ) : null}
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="h-24 text-center">
                Loading...
              </TableCell>
            </TableRow>
          ) : isEmpty ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="h-24 text-center">
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow>
                  {row.getVisibleCells().map((cell) => {
                    const tableCellProps = (cell.column.columnDef.meta as MetaProps | undefined)?.tableCellProps ?? {};
                    const { className: cellClassName, ...restTableCellProps } = tableCellProps;
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(tableCellProps.align === 'right' && 'text-right', cellClassName)}
                        {...restTableCellProps}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {expandedIds[row.id] ? (
                  <TableRow className="hover:bg-white">
                    <TableCell colSpan={columnCount} className="p-0">
                      <EventItemsApprovedSubstitutes
                        clientItemMasterId={row.original.client_item_master_id}
                        erpId={row.original.client_erp_item_id}
                      />
                    </TableCell>
                  </TableRow>
                ) : null}
              </React.Fragment>
            ))
          )}
        </TableBody>
      </Table>
      {resultSetSize >= pageSize ? (
        <ControlledPagination
          page={pageNumber}
          pageSize={pageSize}
          total={resultSetSize}
          onPageChange={setPageNumber}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[25, 50, 100]}
        />
      ) : null}
    </div>
  );
}
